/**
=========================================================
* WhatsApp Scheduler - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import axios from 'axios';  
import React from 'react';  
import { useLocation, Link } from "react-router-dom";
import { FaEdit, FaTrash, FaPlay, FaPause, FaCalendarAlt, FaClock, FaMessage } from "react-icons/fa";

// @mui material components
import Card from "@mui/material/Card";
import SoftInput from "components/SoftInput";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { Helmet } from "react-helmet";
import FormSelect from 'react-bootstrap/FormSelect';

// Soft UI Dashboard React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import {useEffect, useState, useRef} from 'react';
import Modal from 'react-modal';
import Button from 'react-bootstrap/Button';

import SoftAvatar from "components/SoftAvatar";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";

import { Navigate } from "react-router-dom";

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
};

export default class WhatsAppScheduler extends React.Component {  
  constructor(props) {
    super(props);
    this.setState({loggedIn: '1'});
    this.setState({showModal: false});
    this.setState({viewstatus: 'list'});
    this.setState({selectedMessage: null});

    this.state = { 
      loggedIn: '1',
      showModal: false,
      viewstatus: 'list', 
      saveLoading: false,
      selectedMessage: null,
      scheduledMessages: [],
      availableGroups: [],
      repeatType: 'none', // 'none', 'daily', 'weekly'
      repeatUntil: '',
      loading: false,
      accessDenied: false,
      userRole: null
    };
  }

  // Fetch all scheduled messages
  fetchScheduledMessages = () => {
    this.setState({ loading: true });
    axios.get(global.config.app.backend_api_url + 'whatsapp_scheduler/get_all', { withCredentials: true })
      .then(res => {
        if(res.data.status === 1) {
          // Check user role - only allow role_id = 1
          const userRole = res.data.user_details?.role_id;
          this.setState({ userRole: userRole });
          
          if (userRole !== 1) {
            this.setState({ 
              accessDenied: true,
              loading: false
            });
            return;
          }
          
          this.setState({ 
            scheduledMessages: res.data.scheduled_messages || [],
            loading: false,
            accessDenied: false
          });
        } else {
          console.error('Error fetching scheduled messages:', res.data.message);
          alert('Error loading scheduled messages: ' + res.data.message);
          this.setState({ loading: false });
        }
      })
      .catch(error => {
        console.error('Error fetching scheduled messages:', error);
        alert('Error loading scheduled messages. Please try again.');
        this.setState({ loading: false });
      });
  };

  // Fetch available WhatsApp groups
  fetchAvailableGroups = () => {
    axios.get(global.config.app.backend_api_url + 'whatsapp_scheduler/get_available_groups', { withCredentials: true })
      .then(res => {
        if(res.data.status === 1) {
          this.setState({ 
            availableGroups: res.data.available_groups || []
          });
        } else {
          console.error('Error fetching available groups:', res.data.message);
        }
      })
      .catch(error => {
        console.error('Error fetching available groups:', error);
      });
  };

  // Generate repeat schedules
  generateRepeatSchedules = (baseDate, baseTime, repeatType, repeatUntil) => {
    const schedules = [];
    const startDate = new Date(baseDate);
    const endDate = repeatUntil ? new Date(repeatUntil) : null;
    
    let currentDate = new Date(startDate);
    
    while (!endDate || currentDate <= endDate) {
      schedules.push({
        scheduled_date: currentDate.toISOString().split('T')[0],
        scheduled_time: baseTime
      });
      
      // Move to next occurrence
      if (repeatType === 'daily') {
        currentDate.setDate(currentDate.getDate() + 1);
      } else if (repeatType === 'weekly') {
        currentDate.setDate(currentDate.getDate() + 7);
      } else {
        break; // No repeat
      }
    }
    
    return schedules;
  };

  // Create scheduled message(s)
  createScheduledMessage = () => {
    this.setState({ saveLoading: true });
    
    const whatsapp_chat_id = document.getElementById('whatsapp_chat_id').value;
    const message = document.getElementById('message').value;
    const scheduled_date = document.getElementById('scheduled_date').value;
    const scheduled_time = document.getElementById('scheduled_time').value;
    const status = document.getElementById('status').value;
    const repeatType = this.state.repeatType;
    const repeatUntil = this.state.repeatUntil;

    if (!whatsapp_chat_id || !message || !scheduled_date || !scheduled_time) {
      alert('Please fill in all required fields');
      this.setState({ saveLoading: false });
      return;
    }

    // Generate schedules based on repeat settings
    const schedules = this.generateRepeatSchedules(scheduled_date, scheduled_time, repeatType, repeatUntil);
    
    // Create promises for all schedules
    const createPromises = schedules.map(schedule => {
      const post_data = {
        whatsapp_chat_id: whatsapp_chat_id,
        message: message,
        scheduled_date: schedule.scheduled_date,
        scheduled_time: schedule.scheduled_time,
        status: status || 1
      };

      return axios.post(global.config.app.backend_api_url + 'whatsapp_scheduler/create', post_data, { withCredentials: true });
    });

    // Execute all create operations
    Promise.all(createPromises)
      .then(responses => {
        const successful = responses.filter(res => res.data.status === 1);
        const failed = responses.filter(res => res.data.status === 0);
        
        if (successful.length === schedules.length) {
          alert(`Successfully created ${successful.length} scheduled message(s)!`);
        } else if (successful.length > 0) {
          alert(`Created ${successful.length} out of ${schedules.length} scheduled messages. ${failed.length} failed.`);
        } else {
          alert('Failed to create scheduled messages. Please try again.');
        }
        
        this.fetchScheduledMessages();
        this.setState({viewstatus: 'list', saveLoading: false});
      })
      .catch(error => {
        console.error('Error creating scheduled messages:', error);
        alert('Error creating scheduled messages. Please try again.');
        this.setState({ saveLoading: false });
      });
  };

  // Update scheduled message
  updateScheduledMessage = () => {
    this.setState({ saveLoading: true });
    
    const id = this.state.selectedMessage?.id;
    const whatsapp_chat_id = document.getElementById('edit_whatsapp_chat_id').value;
    const message = document.getElementById('edit_message').value;
    const scheduled_date = document.getElementById('edit_scheduled_date').value;
    const scheduled_time = document.getElementById('edit_scheduled_time').value;
    const status = document.getElementById('edit_status').value;

    if (!id || !whatsapp_chat_id || !message || !scheduled_date || !scheduled_time) {
      alert('Please fill in all required fields');
      this.setState({ saveLoading: false });
      return;
    }

    const post_data = {
      id: id,
      whatsapp_chat_id: whatsapp_chat_id,
      message: message,
      scheduled_date: scheduled_date,
      scheduled_time: scheduled_time,
      status: status || 1
    };

    axios.post(global.config.app.backend_api_url + 'whatsapp_scheduler/update', post_data, { withCredentials: true })
      .then(res => {
        if(res.data.status === 1) {
          alert('Scheduled message updated successfully!');
          this.fetchScheduledMessages();
          this.setState({viewstatus: 'list', saveLoading: false});
        } else {
          alert('Error updating scheduled message: ' + res.data.message);
          this.setState({ saveLoading: false });
        }
      })
      .catch(error => {
        console.error('Error updating scheduled message:', error);
        alert('Error updating scheduled message. Please try again.');
        this.setState({ saveLoading: false });
      });
  };

  // Delete scheduled message
  deleteScheduledMessage = (id) => {
    if (window.confirm('Are you sure you want to delete this scheduled message?')) {
      const post_data = { id: id };
      
      axios.post(global.config.app.backend_api_url + 'whatsapp_scheduler/delete', post_data, { withCredentials: true })
        .then(res => {
          if(res.data.status === 1) {
            alert('Scheduled message deleted successfully!');
            this.fetchScheduledMessages();
          } else {
            alert('Error deleting scheduled message: ' + res.data.message);
          }
        })
        .catch(error => {
          console.error('Error deleting scheduled message:', error);
          alert('Error deleting scheduled message. Please try again.');
        });
    }
  };

  // Update message status
  updateMessageStatus = (id, newStatus) => {
    const post_data = { 
      id: id, 
      status: newStatus 
    };
    
    axios.post(global.config.app.backend_api_url + 'whatsapp_scheduler/update_status', post_data, { withCredentials: true })
      .then(res => {
        if(res.data.status === 1) {
          alert('Message status updated successfully!');
          this.fetchScheduledMessages();
        } else {
          alert('Error updating message status: ' + res.data.message);
        }
      })
      .catch(error => {
        console.error('Error updating message status:', error);
        alert('Error updating message status. Please try again.');
      });
  };

  // Edit message
  editMessage = (message) => {
    this.setState({
      selectedMessage: message,
      viewstatus: 'edit'
    });
  };

  componentDidMount() {  
    // Check authentication
    const post_data = { lo : 1};

    axios.post( global.config.app.backend_api_url + `login/sessionavailable`,post_data, { withCredentials: true })
      .then(res => {  
        console.log(res.data)
        var user_id = res.data.user_details.user_id;
        
        if (user_id !== undefined && user_id !== null && user_id !== 'null') 
        {
          // User is authenticated, fetch data
          this.fetchScheduledMessages();
          this.fetchAvailableGroups();
        } else {
          window.location.replace('sign-in');
        }
      });

  }  

  createRows(scheduledMessages) {
    const plainObj = [];
    
    for(let i = 0; i < scheduledMessages.length; i++) {
      const message = scheduledMessages[i];
      
      // Debug logging to understand the data structure
      console.log('Message data:', {
        id: message.id,
        status: message.status,
        statusType: typeof message.status,
        run_flag: message.run_flag,
        runFlagType: typeof message.run_flag
      });
      
      // Handle both string and number status values
      const statusValue = parseInt(message.status);
      const runFlagValue = parseInt(message.run_flag);
      
      console.log('Parsed values:', {
        statusValue,
        runFlagValue
      });
      
      const statusColor = statusValue === 1 ? 'success' : 'error';
      const statusText = statusValue === 1 ? 'Active' : 'Inactive';
      const runStatusColor = runFlagValue === 1 ? 'success' : 'warning';
      const runStatusText = runFlagValue === 1 ? 'Sent' : 'Pending';
      
      const arrayLike = {
        'whatsapp_group': message.whatsapp_group_name || message.whatsapp_chat_id,
        'message': (
          <SoftBox>
            <SoftTypography variant="caption" color="dark" fontWeight="medium">
              {message.message.length > 50 ? message.message.substring(0, 50) + '...' : message.message}
            </SoftTypography>
          </SoftBox>
        ),
        'scheduled_date': (
          <SoftBox display="flex" alignItems="center">
            <FaCalendarAlt style={{ marginRight: '8px', color: '#17c1e8' }} />
            <SoftTypography variant="caption" color="secondary" fontWeight="medium">
              {message.scheduled_date}
            </SoftTypography>
          </SoftBox>
        ),
        'scheduled_time': (
          <SoftBox display="flex" alignItems="center">
            <FaClock style={{ marginRight: '8px', color: '#17c1e8' }} />
            <SoftTypography variant="caption" color="secondary" fontWeight="medium">
              {message.scheduled_time}
            </SoftTypography>
          </SoftBox>
        ),
        'status': (
          <SoftBadge color={statusColor} variant="gradient" size="sm">
            {statusText}
          </SoftBadge>
        ),
        'run_status': (
          <SoftBadge color={runStatusColor} variant="gradient" size="sm">
            {runStatusText}
          </SoftBadge>
        ),
        'action': (
          <SoftBox display="flex" gap={1}>
            <SoftBox
              component="a"
              href="javascript:void(0)"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                color: '#1976d2',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.2)',
                  transform: 'scale(1.1)',
                },
              }}
              title="Edit Message"
              onClick={() => this.editMessage(message)}
            >
              <FaEdit size={14} />
            </SoftBox>
            
            <SoftBox
              component="a"
              href="javascript:void(0)"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: statusValue === 1 ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                color: statusValue === 1 ? '#f44336' : '#4caf50',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: statusValue === 1 ? 'rgba(244, 67, 54, 0.2)' : 'rgba(76, 175, 80, 0.2)',
                  transform: 'scale(1.1)',
                },
              }}
              title={statusValue === 1 ? "Deactivate Message" : "Activate Message"}
              onClick={() => this.updateMessageStatus(message.id, statusValue === 1 ? 0 : 1)}
            >
              {statusValue === 1 ? <FaPause size={14} /> : <FaPlay size={14} />}
            </SoftBox>
            
            <SoftBox
              component="a"
              href="javascript:void(0)"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: 'rgba(156, 39, 176, 0.1)',
                color: '#9c27b0',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(156, 39, 176, 0.2)',
                  transform: 'scale(1.1)',
                },
              }}
              title="Delete Message"
              onClick={() => this.deleteScheduledMessage(message.id)}
            >
              <FaTrash size={14} />
            </SoftBox>
          </SoftBox>
        ),
        'id': message.id
      };

      Array.prototype.push.call(plainObj, arrayLike);
    }
    
    return plainObj;
  }

  schedulerTableData = {
    columns: [
      { name: "whatsapp_group", align: "left" },
      { name: "message", align: "left" },
      { name: "scheduled_date", align: "center" },
      { name: "scheduled_time", align: "center" },
      { name: "status", align: "center" },
      { name: "run_status", align: "center" },
      { name: "action", align: "center" },
    ],
    rows: [],
  };

  render() {  
    if(this.state.loggedIn !== '1') {
      return <Navigate to="/sign-in" />
    };

    // Show access denied page if user doesn't have role_id = 1
    if(this.state.accessDenied) {
      return (
        <DashboardLayout>
          <Helmet>
            <title>Access Denied | {global.config.app.name}</title>
          </Helmet>
          <DashboardNavbar />
          <SoftBox py={3}>
            <SoftBox mb={3}>
              <Card>
                <SoftBox p={4} textAlign="center">
                  <SoftTypography variant="h3" fontWeight="bold" color="error" mb={2}>
                    🔒 Access Denied
                  </SoftTypography>
                  <SoftTypography variant="h6" color="secondary" mb={3}>
                    You don't have permission to access the WhatsApp Scheduler.
                  </SoftTypography>
                  <SoftTypography variant="body2" color="secondary" mb={4}>
                    This feature is restricted to administrators only. 
                    {this.state.userRole && (
                      <span> Your current role ID: {this.state.userRole}</span>
                    )}
                  </SoftTypography>
                  <SoftButton 
                    variant="gradient" 
                    color="info" 
                    onClick={() => window.history.back()}
                  >
                    Go Back
                  </SoftButton>
                </SoftBox>
              </Card>
            </SoftBox>
          </SoftBox>
          <Footer />
        </DashboardLayout>
      );
    }

    const { columns } = this.schedulerTableData;
    const rows = this.createRows(this.state.scheduledMessages);

    return (  
      <DashboardLayout>
        <Helmet>
          <title>WhatsApp Scheduler | {global.config.app.name}</title>
          <meta
            name="description"
            content="Schedule WhatsApp messages with repeat functionality. Create daily or weekly recurring messages."
          />
          <meta
            name="keywords"
            content="WhatsApp, scheduler, messages, automation, repeat"
          />
        </Helmet>
        <DashboardNavbar />
        <SoftBox py={3}>
          <SoftBox mb={3}>
            {
              (() => {
                if(this.state.viewstatus === 'list' ){ 
                 return (
          <Card>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
              <SoftTypography variant="h6">WhatsApp Message Scheduler</SoftTypography>
              
              <SoftButton 
                variant="gradient" 
                size="small" 
                color="info" 
                onClick={() => {
                  this.setState({viewstatus: 'create', selectedMessage: null});
                  this.fetchAvailableGroups();
                }}
              >
                Schedule New Message
              </SoftButton>
            </SoftBox>
            
            {this.state.loading ? (
              <SoftBox p={4} textAlign="center">
                <SoftTypography variant="body2" color="secondary">
                  Loading scheduled messages...
                </SoftTypography>
              </SoftBox>
            ) : (
              <SoftBox
                sx={{
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                <Table columns={columns} rows={rows} />
              </SoftBox>
            )}
          </Card>
                )}
                else if(this.state.viewstatus === 'create' )
                { 
                  return (
                    <Card>
                      <SoftBox p={3} mb={1} textAlign="center">
                        <SoftTypography variant="h5" fontWeight="medium">
                          Schedule WhatsApp Message
                        </SoftTypography>
                      </SoftBox>
                      
                      <SoftBox pt={2} pb={3} px={3}>
                        <SoftBox component="form" role="form" id="schedulerform">
                          
                          {/* WhatsApp Group Selection */}
                          <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="medium" color="dark" mb={1}>
                              WhatsApp Group *
                            </SoftTypography>
                            <FormSelect name="whatsapp_chat_id" id="whatsapp_chat_id" required>
                              <option value="">Select WhatsApp Group</option>
                              {this.state.availableGroups.map((group, index) => (
                                <option key={index} value={group.whatsapp_chat_id}>
                                  {group.name} ({group.whatsapp_chat_id})
                                </option>
                              ))}
                            </FormSelect>
                          </SoftBox>

                          {/* Message Content */}
                          <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="medium" color="dark" mb={1}>
                              Message Content *
                            </SoftTypography>
                            <textarea
                              name="message"
                              id="message"
                              placeholder="Enter your WhatsApp message here..."
                              required
                              style={{
                                width: '100%',
                                height: '100px',
                                padding: '12px',
                                border: '1px solid #d2d6da',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontFamily: 'inherit',
                                resize: 'vertical',
                                outline: 'none'
                              }}
                            />
                          </SoftBox>

                          {/* Date and Time */}
                          <SoftBox display="flex" gap={2} mb={2}>
                            <SoftBox flex={1}>
                              <SoftTypography variant="button" fontWeight="medium" color="dark" mb={1}>
                                Scheduled Date *
                              </SoftTypography>
                              <SoftInput 
                                type="date" 
                                name="scheduled_date" 
                                id="scheduled_date" 
                                required
                              />
                            </SoftBox>
                            <SoftBox flex={1}>
                              <SoftTypography variant="button" fontWeight="medium" color="dark" mb={1}>
                                Scheduled Time *
                              </SoftTypography>
                              <SoftInput 
                                type="time" 
                                name="scheduled_time" 
                                id="scheduled_time" 
                                required
                              />
                            </SoftBox>
                          </SoftBox>

                          {/* Repeat Settings */}
                          <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="medium" color="dark" mb={1}>
                              Repeat Settings
                            </SoftTypography>
                            <SoftBox display="flex" gap={2} alignItems="center" mb={2}>
                              <FormSelect 
                                name="repeat_type" 
                                id="repeat_type"
                                value={this.state.repeatType}
                                onChange={(e) => this.setState({repeatType: e.target.value})}
                                style={{ width: '200px' }}
                              >
                                <option value="none">No Repeat</option>
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                              </FormSelect>
                              
                              {this.state.repeatType !== 'none' && (
                                <SoftBox display="flex" alignItems="center" gap={1}>
                                  <SoftTypography variant="caption" color="secondary">
                                    Repeat until:
                                  </SoftTypography>
                                  <SoftInput 
                                    type="date" 
                                    name="repeat_until" 
                                    id="repeat_until"
                                    value={this.state.repeatUntil}
                                    onChange={(e) => this.setState({repeatUntil: e.target.value})}
                                    style={{ width: '180px' }}
                                  />
                                </SoftBox>
                              )}
                            </SoftBox>
                            
                            {this.state.repeatType !== 'none' && (
                              <SoftBox p={2} sx={{ backgroundColor: '#e3f2fd', borderRadius: '4px' }}>
                                <SoftTypography variant="caption" color="info" fontWeight="medium">
                                  ℹ️ {this.state.repeatType === 'daily' ? 'Daily' : 'Weekly'} messages will be created until the specified date.
                                  {!this.state.repeatUntil && ' Please set a repeat until date.'}
                                </SoftTypography>
                              </SoftBox>
                            )}
                          </SoftBox>

                          {/* Status */}
                          <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="medium" color="dark" mb={1}>
                              Status
                            </SoftTypography>
                            <FormSelect name="status" id="status">
                              <option value="1">Active</option>
                              <option value="0">Inactive</option>
                            </FormSelect>
                          </SoftBox>
                          
                          <SoftBox mt={4} mb={1}>
                            <SoftButton 
                              variant="gradient" 
                              color="dark" 
                              fullWidth 
                              onClick={this.createScheduledMessage} 
                              disabled={this.state.saveLoading}
                            >
                              {this.state.saveLoading ? (
                                <img src={require('assets/images/loader.svg').default} alt="Saving..." style={{ height: 24 }} />
                              ) : (
                                'Schedule Message'
                              )}
                            </SoftButton>
                          </SoftBox>
                          
                        </SoftBox>

                        <SoftBox mt={2}>
                          <SoftTypography
                            variant="button"
                            fontWeight="medium"
                            color="dark"
                            component="a"
                            href="javascript:void(0)"
                            onClick={() => this.setState({viewstatus: 'list'})}
                          >
                            ← Back to List
                          </SoftTypography>
                        </SoftBox>
                      </SoftBox>
                    </Card>
                  )
                }
                else if(this.state.viewstatus === 'edit' )
                { 
                  return (
                    <Card>
                      <SoftBox p={3} mb={1} textAlign="center">
                        <SoftTypography variant="h5" fontWeight="medium">
                          Edit Scheduled Message
                        </SoftTypography>
                      </SoftBox>
                      
                      <SoftBox pt={2} pb={3} px={3}>
                        <SoftBox component="form" role="form" id="editschedulerform">
                          
                          {/* WhatsApp Group Selection */}
                          <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="medium" color="dark" mb={1}>
                              WhatsApp Group *
                            </SoftTypography>
                            <FormSelect 
                              name="edit_whatsapp_chat_id" 
                              id="edit_whatsapp_chat_id" 
                              required
                              defaultValue={this.state.selectedMessage?.whatsapp_chat_id || ''}
                            >
                              <option value="">Select WhatsApp Group</option>
                              {this.state.availableGroups.map((group, index) => (
                                <option key={index} value={group.whatsapp_chat_id}>
                                  {group.name} ({group.whatsapp_chat_id})
                                </option>
                              ))}
                            </FormSelect>
                          </SoftBox>

                          {/* Message Content */}
                          <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="medium" color="dark" mb={1}>
                              Message Content *
                            </SoftTypography>
                            <textarea
                              name="edit_message"
                              id="edit_message"
                              placeholder="Enter your WhatsApp message here..."
                              required
                              defaultValue={this.state.selectedMessage?.message || ''}
                              style={{
                                width: '100%',
                                height: '100px',
                                padding: '12px',
                                border: '1px solid #d2d6da',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontFamily: 'inherit',
                                resize: 'vertical',
                                outline: 'none'
                              }}
                            />
                          </SoftBox>

                          {/* Date and Time */}
                          <SoftBox display="flex" gap={2} mb={2}>
                            <SoftBox flex={1}>
                              <SoftTypography variant="button" fontWeight="medium" color="dark" mb={1}>
                                Scheduled Date *
                              </SoftTypography>
                              <SoftInput 
                                type="date" 
                                name="edit_scheduled_date" 
                                id="edit_scheduled_date" 
                                defaultValue={this.state.selectedMessage?.scheduled_date || ''}
                                required
                              />
                            </SoftBox>
                            <SoftBox flex={1}>
                              <SoftTypography variant="button" fontWeight="medium" color="dark" mb={1}>
                                Scheduled Time *
                              </SoftTypography>
                              <SoftInput 
                                type="time" 
                                name="edit_scheduled_time" 
                                id="edit_scheduled_time" 
                                defaultValue={this.state.selectedMessage?.scheduled_time || ''}
                                required
                              />
                            </SoftBox>
                          </SoftBox>

                          {/* Status */}
                          <SoftBox mb={2}>
                            <SoftTypography variant="button" fontWeight="medium" color="dark" mb={1}>
                              Status
                            </SoftTypography>
                            <FormSelect name="edit_status" id="edit_status" defaultValue={this.state.selectedMessage?.status || 1}>
                              <option value="1">Active</option>
                              <option value="0">Inactive</option>
                            </FormSelect>
                          </SoftBox>
                          
                          <SoftBox mt={4} mb={1}>
                            <SoftButton 
                              variant="gradient" 
                              color="dark" 
                              fullWidth 
                              onClick={this.updateScheduledMessage} 
                              disabled={this.state.saveLoading}
                            >
                              {this.state.saveLoading ? (
                                <img src={require('assets/images/loader.svg').default} alt="Saving..." style={{ height: 24 }} />
                              ) : (
                                'Update Message'
                              )}
                            </SoftButton>
                          </SoftBox>
                          
                        </SoftBox>

                        <SoftBox mt={2}>
                          <SoftTypography
                            variant="button"
                            fontWeight="medium"
                            color="dark"
                            component="a"
                            href="javascript:void(0)"
                            onClick={() => this.setState({viewstatus: 'list'})}
                          >
                            ← Back to List
                          </SoftTypography>
                        </SoftBox>
                      </SoftBox>
                    </Card>
                  )
                }
                
              })()
            }
          </SoftBox>
        </SoftBox>
        
        <Footer />
        
      </DashboardLayout>
    )  
  }  
}  
